import { useTranslation } from 'react-i18next';
import { useOperationsPanels } from '../panels/useOperationsPanels';
import { WorkforceSummary } from './WorkforceSummary';
import { TradeMatrix } from './TradeMatrix';
import { SiteLocationMap } from './SiteLocationMap';
import styles from './rightRail.module.css';

const PANEL_TITLES = [
  'activities.panel.workforce.title',
  'activities.panel.tradeMatrix.title',
  'activities.panel.siteMap.title',
] as const;

/**
 * The right-hand rail of the Activities & Issues page: workforce summary,
 * trade matrix and site-location map. Data comes from the Monitor snapshot.
 * When the snapshot is unavailable each panel renders the specific reason
 * (loading / not linked / provider down / unreachable) — there is no mock data.
 */
export interface IRightRailProps {
  siteId: string;
  /** The rail owns how it looks; the page that places it owns how wide it is. */
  className?: string;
}

export function RightRail({ siteId, className }: IRightRailProps) {
  const { t } = useTranslation();
  const state = useOperationsPanels(siteId);
  const railClass = className ? `${styles.rail} ${className}` : styles.rail;

  if (state.status !== 'ready') {
    const message =
      state.status === 'loading' ? t('activities.panel.loading') : t(state.reasonKey);
    return (
      <aside className={railClass} aria-label="Site overview">
        {PANEL_TITLES.map((key) => (
          <section key={key} className={styles.panel}>
            <h3 className={styles.panelTitle}>{t(key)}</h3>
            <p className={styles.empty}>{message}</p>
          </section>
        ))}
      </aside>
    );
  }

  return (
    <aside className={railClass} aria-label="Site overview">
      <WorkforceSummary data={state.panels.workforce} />
      <TradeMatrix rows={state.panels.trades} />
      <SiteLocationMap floors={state.panels.floors} />
    </aside>
  );
}
